import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { ICreateWorkflowRuleDTO, IUpdateWorkflowRuleDTO, IWorkflowRuleSummary } from '../models/workflows.models';
import { WorkflowsService } from '../services/workflows.service';
import * as workflowsActions from './workflows.actions';

@Injectable()
export class WorkflowsEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private workflowsService: WorkflowsService,
    private commonService: CommonService,
    private translate: TranslateService
  ) {}

  getWorkflowRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.GetWorkflowRules),
      tap(() => this.store.dispatch(workflowsActions.SetWorkflowsLoader({ isLoading: true }))),
      switchMap(() =>
        this.workflowsService.getWorkflowRules().pipe(
          map((res: APIResponse<IWorkflowRuleSummary>) =>
            workflowsActions.GetWorkflowRulesSuccess({ response: res })
          ),
          catchError(() => of(workflowsActions.GetWorkflowRulesError())),
          finalize(() =>
            this.store.dispatch(workflowsActions.SetWorkflowsLoader({ isLoading: false }))
          )
        )
      )
    )
  );

  getWorkflowSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.GetWorkflowSchema),
      switchMap(() =>
        this.workflowsService.getSchema().pipe(
          map((schema) => workflowsActions.GetWorkflowSchemaSuccess({ schema })),
          catchError(() => of(workflowsActions.GetWorkflowSchemaError()))
        )
      )
    )
  );

  getWorkflowTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.GetWorkflowTemplates),
      switchMap(() =>
        this.workflowsService.getWorkflowTemplates().pipe(
          map((templates) => workflowsActions.GetWorkflowTemplatesSuccess({ templates })),
          catchError(() => of(workflowsActions.GetWorkflowTemplatesError()))
        )
      )
    )
  );

  deleteWorkflowRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.DeleteWorkflowRule),
      switchMap(({ id }) =>
        this.workflowsService.deleteWorkflowRule(id).pipe(
          map(() => workflowsActions.DeleteWorkflowRuleSuccess({ id })),
          catchError(() => of(workflowsActions.DeleteWorkflowRuleError()))
        )
      )
    )
  );

  toggleWorkflowRuleStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.ToggleWorkflowRuleStatus),
      switchMap(({ id, status }) =>
        this.workflowsService.toggleWorkflowRuleStatus(id, status).pipe(
          map(() => workflowsActions.ToggleWorkflowRuleStatusSuccess({ id, status })),
          catchError(() => of(workflowsActions.ToggleWorkflowRuleStatusError()))
        )
      )
    )
  );

  saveWorkflowRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.SaveWorkflowRule),
      switchMap(({ id, dto, targetStatus }) => {
        const request$ = id
          ? this.workflowsService.updateWorkflowRule(id, dto as IUpdateWorkflowRuleDTO)
          : this.workflowsService.createWorkflowRule(dto as ICreateWorkflowRuleDTO);

        return request$.pipe(
          switchMap((savedRule) => {
            if (savedRule.status === targetStatus) {
              return of(savedRule);
            }

            return this.workflowsService.toggleWorkflowRuleStatus(savedRule.id, targetStatus, true).pipe(
              map(() => ({ ...savedRule, status: targetStatus })),
              catchError(() => {
                const messageKey = targetStatus === 'active'
                  ? 'workflows.builder.save_status_activate_failed'
                  : 'workflows.builder.save_status_deactivate_failed';
                this.commonService.showToast(this.translate.instant(messageKey), 'WARNING');
                return of(savedRule);
              })
            );
          }),
          map((rule) => workflowsActions.SaveWorkflowRuleSuccess({
            rule,
            completedAt: Date.now(),
          })),
          catchError(() => of(workflowsActions.SaveWorkflowRuleError()))
        );
      })
    )
  );

  refreshRulesAfterSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(workflowsActions.SaveWorkflowRuleSuccess),
      map(() => workflowsActions.GetWorkflowRules())
    )
  );
}
